import React, { useState, useRef } from 'react';
import './Retroknob.css';

const RetroKnob = ({ 
  label = "TUNE", 
  minValue = 0, 
  maxValue = 100, 
  defaultValue = 50,
  onChange = () => {},
  showValue = true,
  size = 'medium' // 'small', 'medium', 'large'
}) => {
  const [value, setValue] = useState(defaultValue);
  const [isDragging, setIsDragging] = useState(false);
  const knobRef = useRef(null);

  // Calculate rotation based on value (0-100 maps to -135 to 135 degrees)
  const rotation = ((value - minValue) / (maxValue - minValue)) * 270 - 135;

  const getAngleFromEvent = (e) => {
    if (!knobRef.current) return 0;
    
    const rect = knobRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = e.clientX - centerX;
    const deltaY = e.clientY - centerY;
    
    // Calculate angle in degrees (-180 to 180)
    let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
    
    // Convert to our knob's coordinate system (0° at top, clockwise)
    // atan2 gives us 0° at right, so we adjust
    angle = angle + 90;
    
    // Normalize to -180 to 180
    if (angle > 180) angle -= 360;
    if (angle < -180) angle += 360;
    
    return angle;
  };

  const updateValueFromAngle = (angle) => {
    // Clamp angle to our knob range (-135° to 135°)
    let clampedAngle = Math.max(-135, Math.min(135, angle));
    
    // Convert angle to value (0 to 100)
    const normalizedAngle = (clampedAngle + 135) / 270; // 0 to 1
    const newValue = minValue + (normalizedAngle * (maxValue - minValue));
    
    const roundedValue = Math.round(newValue);
    setValue(roundedValue);
    onChange(roundedValue);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    // Set initial value based on where they clicked
    const angle = getAngleFromEvent(e);
    updateValueFromAngle(angle);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const angle = getAngleFromEvent(e);
    updateValueFromAngle(angle);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    const angle = getAngleFromEvent(mouseEvent);
    updateValueFromAngle(angle);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    const angle = getAngleFromEvent(mouseEvent);
    updateValueFromAngle(angle);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Add/remove event listeners for dragging
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  return (
    <div className={`retro-knob-container ${size}`}>
      <div className="knob-label">{label}</div>
      <div 
        ref={knobRef}
        className="knob-wrapper"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className="knob-base">
          <div 
            className="knob-dial"
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            <div className="knob-indicator"></div>
            <div className="knob-grip"></div>
            <div className="knob-center"></div>
          </div>
        </div>
        <div className="knob-ticks">
          <div className="tick tick-min"></div>
          <div className="tick tick-mid"></div>
          <div className="tick tick-max"></div>
        </div>
      </div>
      {showValue && (
        <div className="knob-value-display">
          <div className="value-screen">
            {value}
          </div>
        </div>
      )}
    </div>
  );
};

export default RetroKnob;